import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Typography,
  Button,
  styled,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Row, Column } from 'further-ui/components/Layout';
import FullPageDrawer from 'components/new/FullPageDrawer';
import CloseButton from 'components/new/FullPageDrawer/CloseButton';
import { validationRegexes, validationMessages } from 'further-ui/utils';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import useCurrentUser from 'hooks/data/currentUser/useCurrentUser';
import { AdminUserRealm } from 'further-types/admin-user';
import Avatar from 'further-ui/components/Avatar';
import UserInformationForm from '../UserInformationForm';
import UserRoleSection from './UserRoleSection';
import VehicleAccessSection from './VehicleAccessSection';
import { VehicleAccess, EditUserFormData } from '../../types';

const VerticalDivider = styled(Divider)({
  height: '100%',
  width: '1px',
  borderLeftWidth: 'thin',
});

const editUserSchema = z.object({
  firstName: z
    .string()
    .min(1, 'Please enter a first name.')
    .regex(validationRegexes.userName, validationMessages.nameValidationError),
  lastName: z
    .string()
    .min(1, 'Please enter a last name.')
    .regex(validationRegexes.userName, validationMessages.nameValidationError),
  auth0RoleId: z.string(),
  vehicleAccess: z.nativeEnum(VehicleAccess),
  vehicleIds: z.array(z.string()).optional(),
  profilePic: z.string().optional(),
});

type Props = {
  open: boolean;
  onClose: () => void;
  userId: string | null;
};

const EditUserDrawer: React.FC<Props> = ({ open, onClose, userId }) => {
  const { profile } = useCurrentUser();
  const firmId = profile.data?.firm?._id;
  const realm = profile.data?.realm;

  // Try to get user from cached list first (faster if already loaded)
  const { data: cachedUsers } = api.adminUser.listCurrentFirmUsers.useQuery(
    {},
    { enabled: false }, // Don't fetch, just read cache
  );
  const cachedUser = cachedUsers?.find((u) => u.id === userId);

  // Only fetch single user if not in cache
  const { data: fetchedUser, isLoading: isLoadingSingleUser } =
    api.adminUser.getUserInCurrentFirm.useQuery(
      { adminUserId: userId ?? '' },
      { enabled: open && !!userId && !cachedUser },
    );

  // Use cached user if available, otherwise use fetched user
  const user = cachedUser ?? fetchedUser;
  const isLoadingUser = !cachedUser && isLoadingSingleUser;

  const defaultValues = user
    ? {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        auth0RoleId: user.roles?.[0]?.auth0RoleId,
        profilePic: user.profilePic,
      }
    : null;

  const updateUser = api.adminUser.updateUserInCurrentFirm.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'User updated successfully',
      onSuccess: onClose,
    }),
  );

  // Find the current firm settings
  const currentFirmSettings = user?.perFirmSettings.find(
    (setting) =>
      (realm === AdminUserRealm.Lp && setting.lpFirmId === firmId) ||
      (realm === AdminUserRealm.Eis && setting.firmId === firmId),
  );

  const isAllVehiclesMember = currentFirmSettings?.isAllVehiclesMember ?? true;
  const vehicleIds = currentFirmSettings?.vehicleIds ?? [];

  const form = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema),
    values: {
      firstName: defaultValues?.firstName ?? '',
      lastName: defaultValues?.lastName ?? '',
      email: defaultValues?.email ?? '',
      auth0RoleId: defaultValues?.auth0RoleId ?? '',
      vehicleAccess: isAllVehiclesMember
        ? VehicleAccess.All
        : VehicleAccess.Selected,
      vehicleIds,
      profilePic: defaultValues?.profilePic,
    },
  });

  const vehicleAccess = form.watch('vehicleAccess');
  const selectedVehicleIds = form.watch('vehicleIds') ?? [];
  const firstName = form.watch('firstName');
  const lastName = form.watch('lastName');

  const onSubmit = async (data: EditUserFormData) => {
    if (!firmId || !realm || !userId) return;

    const perFirmSettings =
      realm === AdminUserRealm.Lp
        ? {
            lpFirmId: firmId,
            isAllVehiclesMember: data.vehicleAccess === VehicleAccess.All,
            vehicleIds:
              data.vehicleAccess === VehicleAccess.Selected
                ? data.vehicleIds ?? []
                : [],
          }
        : {};

    const newAuth0RoleId =
      data.auth0RoleId !== defaultValues?.auth0RoleId
        ? data.auth0RoleId
        : undefined;

    updateUser.mutate({
      adminUserId: userId,
      firstName: data.firstName,
      lastName: data.lastName,
      perFirmSettings,
      newAuth0RoleId,
      profilePic: data.profilePic,
    });
  };

  const toggleVehicle = (vehicleId: string) => {
    const currentIds = form.getValues('vehicleIds') ?? [];
    const newIds = currentIds.includes(vehicleId)
      ? currentIds.filter((id) => id !== vehicleId)
      : [...currentIds, vehicleId];
    form.setValue('vehicleIds', newIds, { shouldDirty: true });
  };

  // Handle no user selected first
  if (!userId) {
    return (
      <FullPageDrawer
        open={open}
        onClose={onClose}
        title="No user selected"
        actions={<></>}
      />
    );
  }

  // Show loading state while fetching user
  if (isLoadingUser || !defaultValues) {
    return (
      <FullPageDrawer
        open={open}
        onClose={onClose}
        title="Loading..."
        actions={<></>}
      >
        <Column spacing="lg" fullWidth>
          <CircularProgress size={20} />
        </Column>
      </FullPageDrawer>
    );
  }

  const drawerTitle = (
    <Row fullWidth centered spacing="sm" justify="space-between">
      <Row spacing="sm">
        <Avatar
          text={`${firstName} ${lastName}`}
          src={user?.profilePicUrl}
          size="lg"
          color={!user?.profilePicUrl ? 'random' : undefined}
        />
        <Typography variant="h4">
          {firstName} {lastName}
        </Typography>
      </Row>
      <CloseButton onClose={onClose} />
    </Row>
  );

  return (
    <FullPageDrawer
      open={open}
      onClose={onClose}
      title={drawerTitle}
      size="lg"
      innerScroll
      actions={
        <Row justify="space-between" spacing="sm">
          <Button variant="outlined" size="large" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={form.handleSubmit(onSubmit)}
            disabled={!form.formState.isDirty || updateUser.isPending}
          >
            Save and exit
          </Button>
        </Row>
      }
    >
      <Column spacing="lg" fullWidth>
        <Divider />
        <Row spacing="xl" fullWidth>
          <UserInformationForm
            control={form.control}
            mode="edit"
            userId={userId}
            currentProfilePicUrl={user?.profilePicUrl}
          />

          <VerticalDivider />

          <Column spacing="xl" fullWidth>
            <UserRoleSection control={form.control} />
            {realm === AdminUserRealm.Lp && (
              <>
                <Divider />
                <VehicleAccessSection
                  control={form.control}
                  vehicleAccess={vehicleAccess}
                  selectedVehicleIds={selectedVehicleIds}
                  onToggleVehicle={toggleVehicle}
                  firmLogo={profile.data?.firm?.investmentFirmLogo}
                />
              </>
            )}
          </Column>
        </Row>
      </Column>
    </FullPageDrawer>
  );
};

export default EditUserDrawer;
